import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../assets/styles/infrastructure_leasing_modal.scss';

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

/**
 * Buyer confirms lease window then accepts seller quotation (requirement-led enquiries).
 */
export default function InfrastructureLeasingAcceptRequirementQuotationModal({
  open,
  onClose,
  listingLabel,
  quotedPrice,
  initialAgreementStartDate,
  initialAgreementEndDate,
  submitting,
  onConfirm,
}) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [additionalTerms, setAdditionalTerms] = useState('');

  useEffect(() => {
    if (!open) return;
    setStart(str(initialAgreementStartDate));
    setEnd(str(initialAgreementEndDate));
    setAdditionalTerms('');
  }, [open, initialAgreementStartDate, initialAgreementEndDate]);

  if (!open) return null;

  const suffix = `accq-${str(listingLabel).slice(0, 12) || 'quotation'}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm({
      agreementStartDate: str(start),
      agreementEndDate: str(end),
      additionalTerms: str(additionalTerms) || null,
    });
  };

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={onClose} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`ile-accq-title-${suffix}`}
          tabIndex={-1}
          onClick={(ev) => ev.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button type="button" className="ile-modal-panel__close" onClick={onClose} aria-label="Close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`ile-accq-title-${suffix}`}>
              Accept seller quotation
            </h2>
            {listingLabel ? <p className="ile-modal-panel__subtitle">{str(listingLabel)}</p> : null}
          </div>

          <form className="ile-modal-panel__body" onSubmit={handleSubmit}>
            {quotedPrice ? (
              <div className="ile-modal-block mb-3">
                <div className="small text-muted mb-1">Quoted price</div>
                <div className="fw-semibold">{str(quotedPrice)}</div>
              </div>
            ) : null}
            <p className="small text-muted mb-3">
              Confirm the formal lease dates. The agreed amount is taken from the seller&apos;s quotation.
            </p>
            <div className="mb-3">
              <label className="form-label small" htmlFor={`ile-accq-start-${suffix}`}>
                Agreement start (YYYY-MM-DD)
              </label>
              <input
                id={`ile-accq-start-${suffix}`}
                type="text"
                className="form-control form-control-sm"
                autoComplete="off"
                value={start}
                onChange={(ev) => setStart(ev.target.value)}
                required
                disabled={submitting}
                placeholder="2026-05-01"
              />
            </div>
            <div className="mb-3">
              <label className="form-label small" htmlFor={`ile-accq-end-${suffix}`}>
                Agreement end (YYYY-MM-DD)
              </label>
              <input
                id={`ile-accq-end-${suffix}`}
                type="text"
                className="form-control form-control-sm"
                autoComplete="off"
                value={end}
                onChange={(ev) => setEnd(ev.target.value)}
                required
                disabled={submitting}
                placeholder="2027-04-30"
              />
            </div>
            <div className="mb-3">
              <label className="form-label small" htmlFor={`ile-accq-notes-${suffix}`}>
                Additional terms (optional)
              </label>
              <textarea
                id={`ile-accq-notes-${suffix}`}
                className="form-control form-control-sm"
                rows={3}
                value={additionalTerms}
                onChange={(ev) => setAdditionalTerms(ev.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="d-flex gap-2 justify-content-end flex-wrap">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-dark btn-sm" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Proceed with quoted offer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalUi, document.body);
}
